/**
 * Image Frame System — Iter 151.
 *
 * Defines the 14 non-destructive shape masks for the new Squarespace-class
 * image editor. Each shape uses CSS `clip-path` so swapping shapes never
 * touches the underlying image — fully reversible.
 *
 * Notes:
 *   • Polygons are normalised to a 100x100 viewport (CSS percentages).
 *   • `inset()` shapes leverage native rounding for buttery-smooth curves.
 *   • Heart / blob / speech are polygon approximations — sharp at zoom but
 *     visually convincing at typical image sizes. Phase 2 will swap these
 *     for proper SVG mask references.
 */
export const SHAPES = {
  rectangle:  { label: 'Rectangle',     clipPath: 'none' },
  square:     { label: 'Square',        clipPath: 'none', aspect: '1 / 1' },
  rounded:    { label: 'Rounded',       clipPath: 'inset(0 round 24px)' },
  pill:       { label: 'Pill',          clipPath: 'inset(0 round 9999px)' },
  circle:     { label: 'Circle',        clipPath: 'circle(50% at 50% 50%)' },
  arch:       { label: 'Arch',          clipPath: 'inset(0 round 50% 50% 0 0)' },
  triangle:   { label: 'Triangle',      clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' },
  diamond:    { label: 'Diamond',       clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' },
  hexagon:    { label: 'Hexagon',       clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' },
  octagon:    { label: 'Octagon',       clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' },
  star:       { label: 'Star',          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' },
  heart:      { label: 'Heart',         clipPath: 'polygon(50% 100%, 0% 38%, 25% 0%, 50% 25%, 75% 0%, 100% 38%)' },
  blob:       { label: 'Blob',          clipPath: 'polygon(60% 4%, 90% 16%, 96% 44%, 92% 72%, 70% 92%, 40% 96%, 14% 80%, 4% 54%, 10% 24%, 32% 8%)' },
  speech:     { label: 'Speech',        clipPath: 'polygon(0% 0%, 100% 0%, 100% 75%, 60% 75%, 50% 95%, 40% 75%, 0% 75%)' },
};

export const SHAPE_KEYS = Object.keys(SHAPES);

/**
 * Display modes — controls how the underlying image fills its frame.
 *   fill   → `object-fit: cover`   (crops edges, no distortion)
 *   fit    → `object-fit: contain` (whole image, may letterbox)
 *   stretch→ `object-fit: fill`    (full frame, distorts)
 */
export const DISPLAY_MODES = [
  { v: 'fill',    label: 'Fill',    desc: 'Cover the frame (may crop)' },
  { v: 'fit',     label: 'Fit',     desc: 'Show entire image' },
  { v: 'stretch', label: 'Stretch', desc: 'Distort to fill' },
];

/** Helpers */
export const defaultFrame = () => ({
  shape: 'rectangle',
  displayMode: 'fill',
  radius: 0,
  borderWidth: 0,
  borderColor: '#FFFFFF',
  borderStyle: 'solid',
  opacity: 1,
  scale: 1,
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
});

export const DEVICES = ['desktop', 'tablet', 'mobile'];

/**
 * Responsive-frame resolver — Iter 153.
 *
 * Storage shape:
 *   • Flat   (Phase 1/2 legacy):  { shape: 'circle', ... }
 *   • Per-device (Phase 4):       { _responsive: true, desktop: {...}, tablet: {...}, mobile: {...} }
 *
 * `getDeviceFrame` always returns the flat frame for the requested device,
 * falling back to desktop, then to defaultFrame(), so existing data keeps
 * working without migration.
 */
export const isResponsiveFrame = (raw) => !!(raw && raw._responsive === true);

export const getDeviceFrame = (raw, device = 'desktop') => {
  if (!raw) return {};
  if (isResponsiveFrame(raw)) {
    return raw[device] || raw.desktop || {};
  }
  return raw; // flat — applies to all devices
};

/**
 * Returns a new "_image_frames[field]" value with the given key updated for
 * the active device. If the source was flat, it's promoted to the responsive
 * shape with all three devices initialised from the existing flat config so
 * Desktop/Tablet/Mobile start as visual mirrors of the previous state.
 */
export const writeDeviceFrame = (raw, device, key, value) => {
  const flatBase = isResponsiveFrame(raw) ? null : (raw || {});
  const responsive = isResponsiveFrame(raw)
    ? { ...raw }
    : {
        _responsive: true,
        desktop: { ...(flatBase || {}) },
        tablet:  { ...(flatBase || {}) },
        mobile:  { ...(flatBase || {}) },
      };
  responsive[device] = { ...(responsive[device] || {}), [key]: value };
  return responsive;
};

/**
 * Replaces the whole frame object for a device (used by Apply/Cancel and
 * Sync-from-Desktop).
 */
export const writeDeviceFrameWhole = (raw, device, next) => {
  const flatBase = isResponsiveFrame(raw) ? null : (raw || {});
  const responsive = isResponsiveFrame(raw)
    ? { ...raw }
    : {
        _responsive: true,
        desktop: { ...(flatBase || {}) },
        tablet:  { ...(flatBase || {}) },
        mobile:  { ...(flatBase || {}) },
      };
  responsive[device] = { ...next };
  return responsive;
};

/**
 * Returns a flat-form copy of the desktop config (or the whole flat config
 * if the source isn't responsive yet). Used to "Sync from Desktop" all
 * downstream devices in one go.
 */
export const desktopAsFlat = (raw) => {
  if (!raw) return {};
  return isResponsiveFrame(raw) ? { ...(raw.desktop || {}) } : { ...raw };
};
